import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
}

const MyAccount: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const data = await apiService.getUserMe();
      setUser(data);
      setForm(prev => ({ ...prev, name: data.name, email: data.email }));
    } catch (err) {
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await apiService.updateUser(String(user?.id || ''), {
        name: form.name
      });
      setSuccess('Profile updated successfully');
      setError(null);
    } catch (err) {
      setError('Failed to update profile');
      setSuccess(null);
    }
  };

  const handleChangePassword = async () => {
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    try {
      await apiService.changePassword(form.currentPassword, form.newPassword);
      setSuccess('Password changed successfully');
      setError(null);
      setForm(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
    } catch (err) {
      setError('Failed to change password');
      setSuccess(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>My Account</Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" mb={2}>Profile Information</Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Email"
            value={form.email}
            disabled
            fullWidth
          />
          <Button variant="contained" onClick={handleUpdateProfile}>
            Update Profile
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>Change Password</Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Current Password"
            type="password"
            value={form.currentPassword}
            onChange={(e) => setForm(prev => ({ ...prev, currentPassword: e.target.value }))}
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={form.newPassword}
            onChange={(e) => setForm(prev => ({ ...prev, newPassword: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
            fullWidth
          />
          <Button variant="contained" onClick={handleChangePassword}>
            Change Password
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default MyAccount; 